import React, { useContext, useEffect, useState } from "react";
import ContentLayout from "../../component/ContentLayout";
import axios from "../../util/axios";
import { Context } from "../../component/Context/ErrorContext";
import moment from "moment";
import CustomModal from "../../component/Modal";
import CustomButton from "../../component/Button";
import CustomTextField from "../../component/TextField";
import { IoAdd, IoTrash, IoCreateOutline } from "react-icons/io5";
import { VscLoading } from "react-icons/vsc";

interface Holiday {
  id: number;
  date: string;
  name: string;
  description?: string;
}

interface CalendarSettings {
  alternateSaturdayOff: boolean;
  offDays: string[];
  holidays: Holiday[];
}

const CalendarSettings = () => {
  const { setError } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<CalendarSettings>({
    alternateSaturdayOff: false,
    offDays: [],
    holidays: [],
  });
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayName, setHolidayName] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const allDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  useEffect(() => {
    fetchCalendarSettings();
  }, []);

  const fetchCalendarSettings = async () => {
    setLoading(true);
    try {
      const result = await axios({
        URL: "/omw/fetch-calendar-settings",
        method: "GET",
      });
      if (result?.data?.data) {
        setSettings(result.data.data);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to fetch calendar settings");
    } finally {
      setLoading(false);
    }
  };

  const updateCalendarSettings = async () => {
    setLoading(true);
    try {
      await axios({
        URL: "/omw/update-calendar-settings",
        method: "POST",
        data: {
          alternateSaturdayOff: settings.alternateSaturdayOff,
          offDays: settings.offDays,
        },
      });
      setError("Calendar settings updated successfully!");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update calendar settings");
    } finally {
      setLoading(false);
    }
  };

  const handleOffDayToggle = (day: string) => {
    setSettings((prev) => {
      const newOffDays = prev.offDays.includes(day)
        ? prev.offDays.filter((d) => d !== day)
        : [...prev.offDays, day];
      return { ...prev, offDays: newOffDays };
    });
  };

  const handleAlternateSaturdayToggle = () => {
    setSettings((prev) => ({
      ...prev,
      alternateSaturdayOff: !prev.alternateSaturdayOff,
    }));
  };

  const handleDateClick = (date: string) => {
    const existingHoliday = settings.holidays.find((h) => h.date === date);
    if (existingHoliday) {
      setEditingHoliday(existingHoliday);
      setHolidayName(existingHoliday.name);
      setHolidayDescription(existingHoliday.description || "");
    } else {
      setEditingHoliday(null);
      setHolidayName("");
      setHolidayDescription("");
    }
    setSelectedDate(date);
    setShowHolidayModal(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayName.trim() || !selectedDate) {
      setError("Please enter holiday name");
      return;
    }

    setLoading(true);
    try {
      if (editingHoliday) {
        // Update existing holiday
        await axios({
          URL: `/omw/update-holiday/${editingHoliday.id}`,
          method: "PUT",
          data: {
            date: selectedDate,
            name: holidayName,
            description: holidayDescription,
          },
        });
        setError("Holiday updated successfully!");
      } else {
        // Add new holiday
        await axios({
          URL: "/omw/add-holiday",
          method: "POST",
          data: {
            date: selectedDate,
            name: holidayName,
            description: holidayDescription,
          },
        });
        setError("Holiday added successfully!");
      }
      setShowHolidayModal(false);
      setSelectedDate(null);
      setHolidayName("");
      setHolidayDescription("");
      setEditingHoliday(null);
      fetchCalendarSettings();
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) {
      return;
    }

    setLoading(true);
    try {
      await axios({
        URL: `/omw/delete-holiday/${id}`,
        method: "DELETE",
      });
      setError("Holiday deleted successfully!");
      fetchCalendarSettings();
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to delete holiday");
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const startDate = startOfMonth.clone().startOf("week");
    const endDate = endOfMonth.clone().endOf("week");

    const days = [];
    let currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(endDate, "day")) {
      days.push(currentDate.clone());
      currentDate.add(1, "day");
    }

    return days;
  };

  const getDayClass = (date: moment.Moment) => {
    const dateStr = date.format("YYYY-MM-DD");
    const dayName = date.format("dddd").toLowerCase();
    const isSaturday = date.day() === 6;
    const isHoliday = settings.holidays.some((h) => h.date === dateStr);
    const isOffDay = settings.offDays.includes(dayName);
    const isAlternateSaturdayOff =
      settings.alternateSaturdayOff && isSaturday && date.date() % 2 === 0;

    let classes = "w-full h-20 p-2 border border-gray/30 flex flex-col items-start justify-start cursor-pointer hover:bg-gray/50 transition-colors rounded";

    if (isHoliday) {
      classes += " bg-red-500/20 border-red-500/50";
    } else if (isAlternateSaturdayOff) {
      classes += " bg-orange-500/20 border-orange-500/50";
    } else if (isOffDay) {
      classes += " bg-blue-500/20 border-blue-500/50";
    } else {
      classes += " bg-primary";
    }

    if (!currentMonth.isSame(date, "month")) {
      classes += " opacity-40";
    }

    return classes;
  };

  const getDayLabel = (date: moment.Moment) => {
    const dateStr = date.format("YYYY-MM-DD");
    const holiday = settings.holidays.find((h) => h.date === dateStr);
    if (holiday) {
      return holiday.name;
    }
    return "";
  };

  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, "month"));
  };

  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, "month"));
  };

  const calendarDays = getCalendarDays();

  return (
    <ContentLayout className={"items-start rounded-3xl m-2 gap-6 overflow-y-scroll"}>
      <div className="w-full flex justify-between items-center">
        <p className={"text-3xl font-bold text-primaryText"}>Calendar Settings</p>
      </div>

      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <div className="lg:col-span-2 bg-primary rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={previousMonth}
              className="text-primaryText hover:text-sidebarSelectText text-xl font-bold"
            >
              ←
            </button>
            <h2 className="text-primaryText text-xl font-bold">
              {currentMonth.format("MMMM YYYY")}
            </h2>
            <button
              onClick={nextMonth}
              className="text-primaryText hover:text-sidebarSelectText text-xl font-bold"
            >
              →
            </button>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="text-center text-primaryText font-semibold text-sm py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => (
              <div
                key={index}
                className={getDayClass(date)}
                onClick={() => handleDateClick(date.format("YYYY-MM-DD"))}
              >
                <span className="text-primaryText text-sm font-medium">
                  {date.format("D")}
                </span>
                {getDayLabel(date) && (
                  <span className="text-xs text-primaryText mt-1 truncate w-full">
                    {getDayLabel(date)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-primary border border-gray/30 rounded"></div>
              <span className="text-primaryText">Regular Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500/20 border border-blue-500/50 rounded"></div>
              <span className="text-primaryText">Off Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500/20 border border-orange-500/50 rounded"></div>
              <span className="text-primaryText">Alternate Saturday Off</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500/20 border border-red-500/50 rounded"></div>
              <span className="text-primaryText">Holiday</span>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="bg-primary rounded-xl p-6">
          <h3 className="text-primaryText text-xl font-bold mb-6">Settings</h3>

          {/* Alternate Saturday Toggle */}
          <div className="mb-6">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-primaryText font-medium">Alternate Saturday Off</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={settings.alternateSaturdayOff}
                  onChange={handleAlternateSaturdayToggle}
                  className="sr-only"
                />
                <div
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.alternateSaturdayOff ? "bg-sidebarSelectText" : "bg-gray"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                      settings.alternateSaturdayOff ? "translate-x-6" : "translate-x-0.5"
                    } mt-0.5`}
                  ></div>
                </div>
              </div>
            </label>
          </div>

          {/* Off Days Selection */}
          <div className="mb-6">
            <label className="text-primaryText font-medium block mb-3">Off Days</label>
            <div className="flex flex-col gap-2">
              {allDays.map((day) => (
                <label
                  key={day}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={settings.offDays.includes(day)}
                    onChange={() => handleOffDayToggle(day)}
                    className="w-4 h-4 text-sidebarSelectText bg-gray border-grayBorder rounded focus:ring-sidebarSelectText"
                  />
                  <span className="text-primaryText capitalize">{day}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Save Settings Button */}
          <CustomButton
            title={loading ? <VscLoading className="animate-spin" /> : "Save Settings"}
            onClick={updateCalendarSettings}
            disabled={loading}
            className="w-full !py-3"
          />

          {/* Holidays List */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-primaryText font-medium">Holidays</h4>
              <button
                onClick={() => {
                  setSelectedDate(moment().format("YYYY-MM-DD"));
                  setEditingHoliday(null);
                  setHolidayName("");
                  setHolidayDescription("");
                  setShowHolidayModal(true);
                }}
                className="text-sidebarSelectText hover:text-blue-400 flex items-center gap-1"
              >
                <IoAdd className="text-lg" />
                <span className="text-sm">Add</span>
              </button>
            </div>
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
              {settings.holidays.length === 0 ? (
                <p className="text-grayLight text-sm">No holidays added</p>
              ) : (
                settings.holidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="bg-gray rounded-lg p-3 flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <p className="text-primaryText font-medium text-sm">{holiday.name}</p>
                      <p className="text-grayLight text-xs">
                        {moment(holiday.date).format("MMM DD, YYYY")}
                      </p>
                      {holiday.description && (
                        <p className="text-grayLight text-xs mt-1">{holiday.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingHoliday(holiday);
                          setHolidayName(holiday.name);
                          setHolidayDescription(holiday.description || "");
                          setSelectedDate(holiday.date);
                          setShowHolidayModal(true);
                        }}
                        className="text-sidebarSelectText hover:text-blue-400"
                      >
                        <IoCreateOutline className="text-lg" />
                      </button>
                      <button
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <IoTrash className="text-lg" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Holiday Modal */}
      <CustomModal
        open={showHolidayModal}
        onClose={() => {
          setShowHolidayModal(false);
          setSelectedDate(null);
          setEditingHoliday(null);
          setHolidayName("");
          setHolidayDescription("");
        }}
        label={editingHoliday ? "Edit Holiday" : "Add Holiday"}
      >
        <div className="flex flex-col gap-4 mt-4">
          <div>
            <label className="text-primaryText text-sm font-medium block mb-2">
              Date
            </label>
            <input
              type="date"
              value={selectedDate || ""}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-3 bg-gray text-primaryText rounded-lg border border-grayBorder focus:outline-none focus:border-sidebarSelectText"
              disabled={!!editingHoliday}
            />
          </div>
          <CustomTextField
            label="Holiday Name"
            type="text"
            name="holidayName"
            value={holidayName}
            onChange={(e: any) => setHolidayName(e.target.value)}
            required
          />
          <CustomTextField
            label="Description (Optional)"
            type="text"
            name="holidayDescription"
            value={holidayDescription}
            onChange={(e: any) => setHolidayDescription(e.target.value)}
          />
          <CustomButton
            title={loading ? <VscLoading className="animate-spin" /> : editingHoliday ? "Update Holiday" : "Add Holiday"}
            onClick={handleSaveHoliday}
            disabled={loading}
            className="!py-3"
          />
        </div>
      </CustomModal>
    </ContentLayout>
  );
};

export default CalendarSettings;

